import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { adminApi } from '../../api';
import { toast } from '../../hooks/use-toast';
import { CheckCircle2, PackageCheck, RotateCcw, Copy } from 'lucide-react';

const statusColor = (s) => s === 'DELIVERED' ? 'text-[#22c55e]' : s === 'PENDING' ? 'text-[#f59e0b]' : 'text-[#8a8a8a]';

const OrdersAdmin = () => {
  const { orders, refreshAll } = useAdmin();
  const [filter, setFilter] = useState('ALL');
  const [expand, setExpand] = useState(null);
  const [cred, setCred] = useState('');

  const list = filter === 'ALL' ? orders : orders.filter((o) => o.status === filter);

  const deliver = async (o) => {
    if (!cred.trim()) { toast({ title: 'Enter credential/key' }); return; }
    try {
      await adminApi.deliverOrder(o.id, cred);
      toast({ title: 'Delivered', description: o.id });
      setCred(''); setExpand(null);
      refreshAll();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const refund = async (o) => {
    if (!window.confirm(`Refund order ${o.id}?`)) return;
    try {
      await adminApi.refundOrder(o.id);
      toast({ title: 'Refunded', description: o.id });
      refreshAll();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a] font-mono">ORDERS</div>
          <div className="text-2xl text-[#e6e6e6] font-mono">{list.length} <span className="text-[10px] text-[#5a5a5a] tracking-[0.25em]">RECORDS</span></div>
        </div>
        <div className="flex gap-1">
          {['ALL', 'PENDING', 'DELIVERED', 'REFUNDED'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-[10px] tracking-[0.2em] rounded-md border font-mono ${filter === f ? 'border-[#22c55e] text-[#22c55e] bg-[#0f1a12]' : 'border-[#1f1f1f] text-[#8a8a8a]'}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="border border-[#151515] rounded-md overflow-hidden font-mono">
        <div className="grid grid-cols-[140px_1.5fr_1fr_1fr_1fr_100px_130px] px-4 py-3 text-[10px] tracking-[0.25em] text-[#5a5a5a] border-b border-[#151515] bg-[#080808]">
          <div>ORDER ID</div>
          <div>PRODUCT</div>
          <div>USER</div>
          <div>AMOUNT</div>
          <div>DATE</div>
          <div className="text-right">STATUS</div>
          <div className="text-right">ACTION</div>
        </div>
        {list.length === 0 && <div className="px-4 py-8 text-center text-[12px] text-[#5a5a5a]">No orders.</div>}
        {list.map((o) => (
          <React.Fragment key={o.id}>
            <div className="grid grid-cols-[140px_1.5fr_1fr_1fr_1fr_100px_130px] px-4 py-3 text-[12px] border-b border-[#0f0f0f] last:border-0 items-center hover:bg-[#0d0d0d]">
              <div className="text-[#22c55e] truncate pr-2">{o.id}</div>
              <div className="text-[#e6e6e6] uppercase truncate pr-3">{o.product_name}</div>
              <div className="text-[#8a8a8a] truncate pr-2">@{o.user?.username || '-'}</div>
              <div className="text-[#e6e6e6]">{o.currency === 'USDT' ? '$' : '₹'}{Math.abs(o.amount).toFixed(2)}</div>
              <div className="text-[#8a8a8a] text-[11px]">{o.created_at}</div>
              <div className={`text-right tracking-[0.2em] ${statusColor(o.status)}`}>{o.status}</div>
              <div className="text-right flex justify-end gap-1">
                {o.status === 'PENDING' && (
                  <button onClick={() => setExpand(expand === o.id ? null : o.id)} className="px-2 py-1 text-[10px] tracking-[0.2em] rounded border border-[#22c55e] text-[#22c55e] hover:bg-[#0f1a12]">DELIVER</button>
                )}
                {o.status === 'DELIVERED' && (
                  <button onClick={() => refund(o)} className="px-2 py-1 text-[10px] tracking-[0.2em] rounded border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#ef4444] hover:text-[#ef4444]"><RotateCcw size={11} /></button>
                )}
              </div>
            </div>
            {expand === o.id && (
              <div className="px-4 py-3 bg-[#0d0d0d] border-b border-[#0f0f0f]">
                <div className="text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-2">ENTER CREDENTIAL / KEY TO DELIVER</div>
                <textarea value={cred} onChange={(e) => setCred(e.target.value)} rows={3}
                  placeholder="email:password | activation code | download link"
                  className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm text-[#e6e6e6] outline-none focus:border-[#22c55e] resize-none" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => deliver(o)} className="px-3 py-1.5 text-[10px] tracking-[0.25em] rounded bg-[#166534] hover:bg-[#15803d] text-[#e7ffef]"><CheckCircle2 size={11} className="inline mr-1" /> DELIVER</button>
                  <button onClick={() => { setExpand(null); setCred(''); }} className="px-3 py-1.5 text-[10px] tracking-[0.25em] rounded border border-[#1f1f1f] text-[#8a8a8a]">CANCEL</button>
                </div>
              </div>
            )}
            {expand !== o.id && o.credential && o.status === 'DELIVERED' && (
              <div className="px-4 py-2 text-[11px] bg-[#050505] border-b border-[#0f0f0f] flex items-center gap-2 text-[#7a7a7a]">
                <PackageCheck size={12} className="text-[#22c55e]" />
                <span className="text-[#7a7a7a]">Delivered key:</span>
                <code className="text-[#22c55e] truncate max-w-[600px]">{o.credential}</code>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default OrdersAdmin;
